import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useMaintenanceForm } from '@/hooks/residents/useMaintenanceForm';
import { useMaintenanceSubmit } from '@/hooks/residents/useMaintenanceSubmit';
import { useMaintenanceValidation } from '@/hooks/residents/useMaintenanceValidation';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAppSelector } from '@/store';
import { DesktopDropdownSelector, DesktopDropdownOption } from '@/components/common/admin/desktop/DesktopDropdownSelector';
import { MAX_ATTACHMENTS, MAINTENANCE_CONSTANTS } from '@/constants/residents';
import { RadioButton } from '@/components/residents/maintenance/mobile/steps/shared';
import { getAllSubCategories } from '@/utils/residents/maintenance/maintenance';
import { showAlert } from '@/utils/common/alert';
import { MediaCarouselUpload, MediaUploadItem } from '@/components/common/residents/MediaCarouselUpload';

const ChevronLeftIcon = SVG_COMPONENTS.CHEVRON_LEFT;

interface DesktopCreateMaintenanceViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

// Request type options
type RequestType = 'maintenance' | 'it';

/**
 * DesktopCreateMaintenanceView - Centered single-page create form matching Figma
 */
export const DesktopCreateMaintenanceView: React.FC<DesktopCreateMaintenanceViewProps> = ({
  onClose,
  onSuccess,
}) => {
  const { showError } = useToast(); // Needed for MediaCarouselUpload
  const user = useAppSelector((s) => s.auth.user as any);
  const userName = user?.firstName || 'there';

  // Form state from hook
  const {
    description, setDescription,
    location, setLocation,
    category, setCategory,
    subCategory, setSubCategory,
    problem, setProblem,
    severity, setSeverity,
    phoneNumber, setPhoneNumber,
    permissionChoice, setPermissionChoice,
    maintenanceInfo,
    locationOptions,
    categoryOptions,
    subCategoryOptions: _hookSubCategoryOptions,
    problemOptions,
    severityOptions,
    loadInfo,
    isInfoError,
    infoErrorStatus,
  } = useMaintenanceForm();

  // Submission and validation hooks
  const { submitTicket, status } = useMaintenanceSubmit();
  const { validateForm } = useMaintenanceValidation();

  // Local state
  const [requestType, setRequestType] = useState<RequestType>('maintenance');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState<MediaUploadItem[]>([]);
  const [hasPets, setHasPets] = useState<boolean | null>(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [timeSlots, setTimeSlots] = useState<string[]>(['', '', '']);

  // Load maintenance info on mount
  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  // Handle info load error
  useEffect(() => {
    if (isInfoError && (infoErrorStatus === 500 || infoErrorStatus == null)) {
      console.error('Unable to load maintenance options');
    }
  }, [isInfoError, infoErrorStatus]);

  // For mobile, provide ALL subcategories from ALL categories (flattened)
  const allSubCategoryOptions = useMemo(() => getAllSubCategories(maintenanceInfo), [maintenanceInfo]);

  const subCategoryOptions = category ? _hookSubCategoryOptions : allSubCategoryOptions;

  // Form submission using shared hook
  const handleSubmit = useCallback(async () => {
    // Validation using shared hook
    const validationResult = validateForm({
      requestType,
      subCategoryOptions,
      subCategory,
      severity,
      title,
      permissionChoice,
      timeSlots,
    });

    if (!validationResult.valid) {
      showAlert('Required', validationResult.message || 'Please complete all required fields.');
      return;
    }

    const result = await submitTicket({
      requestType,
      category,
      subCategory,
      maintenanceInfo,
      location,
      problem,
      severity,
      title,
      description,
      phoneNumber,
      permissionChoice,
      attachments: files as any,
      hasPets: hasPets ?? undefined,
      specialInstructions,
    });

    if (result.success) {
      onSuccess?.();
      onClose();
    }
  }, [
    requestType, subCategoryOptions, subCategory, severity, title, permissionChoice,
    timeSlots, category, maintenanceInfo, location, problem, description,
    phoneNumber, files, hasPets, specialInstructions,
    validateForm, submitTicket, onSuccess, onClose,
  ]);

  // Use severity options from API or fallback to default labels
  const displaySeverityOptions = severityOptions.length > 0 
    ? severityOptions 
    : MAINTENANCE_CONSTANTS.SEVERITY_FALLBACK_LABELS;

  return (
    <ScrollView 
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={onClose}
          activeOpacity={0.8}
        >
          <ChevronLeftIcon width={24} height={24} color={PRIMITIVE_COLORS.gray300} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>
          Hi {userName}, what do you need help with?
        </Text>
      </View>

      {/* Form Content */}
      <View style={styles.formContainer}>
        {/* Category Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Category</Text>
          <View style={styles.categoryRow}>
            {/* Maintenance Option */}
            <TouchableOpacity
              style={[
                styles.categoryCard,
                requestType === 'maintenance' && styles.categoryCardSelected,
              ]}
              onPress={() => setRequestType('maintenance')}
              activeOpacity={0.8}
            >
              <View style={styles.categoryIconContainer}>
                <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={20} height={20} />
              </View>
              <View style={styles.categoryContent}>
                <View style={styles.categoryHeader}>
                  <Text style={styles.categoryName}>Maintenance</Text>
                  <RadioButton selected={requestType === 'maintenance'} />
                </View>
                <Text style={styles.categoryDescription}>
                  Report issues related to repairs or upkeep
                </Text>
                {requestType === 'maintenance' && subCategoryOptions.length > 0 && (
                  <View style={styles.subcategoryDropdown}>
                    <DesktopDropdownSelector
                      label="Subcategory"
                      placeholder="Subcategory"
                      selectedValue={subCategory}
                      options={subCategoryOptions.map<DesktopDropdownOption>((s) => ({ 
                        label: s, 
                        value: s 
                      }))}
                      onChange={setSubCategory}
                    />
                  </View>
                )}
              </View>
            </TouchableOpacity>

            {/* IT Option */}
            <TouchableOpacity
              style={[
                styles.categoryCard,
                requestType === 'it' && styles.categoryCardSelected,
              ]}
              onPress={() => setRequestType('it')}
              activeOpacity={0.8}
            >
              <View style={styles.categoryIconContainer}>
                <RESIDENTS_SVG_COMPONENTS.WIFI width={20} height={20} color={SEMANTIC_COLORS.text.primary} />
              </View>
              <View style={styles.categoryContent}>
                <View style={styles.categoryHeader}>
                  <Text style={styles.categoryName}>IT</Text>
                  <RadioButton selected={requestType === 'it'} />
                </View>
                <Text style={styles.categoryDescription}>
                  Report issues with the app or system
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        </View>

        {/* Severity Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>How this affects you?</Text>
          <View style={styles.severityGrid}>
            {displaySeverityOptions.map((option, index) => (
              <TouchableOpacity
                key={option}
                style={[
                  styles.severityItem,
                  severity === option && styles.severityItemSelected,
                ]}
                onPress={() => setSeverity(option)}
                activeOpacity={0.8}
              >
                <Text style={styles.severityText}>{option}</Text>
                <RadioButton selected={severity === option} />
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Details Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Tell us what happened</Text>
          <View style={styles.detailsRow}>
            {/* Left Column - Title and Description */}
            <View style={styles.detailsLeft}>
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Brief title</Text>
                <TextInput
                  style={styles.titleInput}
                  value={title}
                  onChangeText={setTitle}
                  placeholder="Enter a brief title"
                  placeholderTextColor={PRIMITIVE_COLORS.gray300}
                />
              </View>
              <View style={[styles.inputContainer, styles.descriptionContainer]}>
                <TextInput
                  style={styles.descriptionInput}
                  value={description}
                  onChangeText={setDescription}
                  placeholder="Description"
                  placeholderTextColor={PRIMITIVE_COLORS.gray300}
                  multiline
                  numberOfLines={10}
                  textAlignVertical="top"
                />
              </View>
            </View>

            {/* Right Column - Photo Upload */}
            <View style={styles.detailsRight}>
              <MediaCarouselUpload
                files={files}
                onFilesChange={setFiles}
                config={{
                  maxFiles: MAX_ATTACHMENTS,
                  maxFileSizeMB: 2,
                  uploadAreaHeight: 384,
                }}
                onError={showError}
                variant="dark"
              />
            </View>
          </View>
        </View>

        {/* Entry Permission Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Can our technician enter your residence?</Text>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioRow}
              onPress={() => setPermissionChoice('yes')}
              activeOpacity={0.8}
            >
              <RadioButton selected={permissionChoice === 'yes'} />
              <Text style={styles.radioLabel}>Yes</Text>
            </TouchableOpacity>
            <View style={styles.radioColumn}>
              <TouchableOpacity
                style={styles.radioRow}
                onPress={() => setPermissionChoice('no')}
                activeOpacity={0.8}
              >
                <RadioButton selected={permissionChoice === 'no'} />
                <Text style={styles.radioLabel}>No</Text>
              </TouchableOpacity>

              {permissionChoice === 'no' && (
                <View style={styles.timeSlotsContainer}>
                  <View style={styles.alertBox}>
                    <Text style={styles.alertIcon}>⚠</Text>
                    <Text style={styles.alertText}>
                      Please select {MAINTENANCE_CONSTANTS.MINIMUM_TIME_SLOTS} time slots in the next 10 days
                    </Text>
                  </View>
                  {[0, 1, 2].map((index) => (
                    <View key={index} style={styles.timeSlotInput}>
                      <Text style={styles.timeSlotLabel}>Date and time</Text>
                      <TextInput
                        style={styles.timeSlotValue}
                        value={timeSlots[index]}
                        onChangeText={(text) => {
                          const newSlots = [...timeSlots];
                          newSlots[index] = text;
                          setTimeSlots(newSlots);
                        }}
                        placeholder="MM/DD/YYYY HH:MM AM"
                        placeholderTextColor={PRIMITIVE_COLORS.gray300}
                      />
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Pets Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Do you have pets?</Text>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioRow}
              onPress={() => setHasPets(true)}
              activeOpacity={0.8}
            >
              <RadioButton selected={hasPets === true} />
              <Text style={styles.radioLabel}>Yes</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.radioRow}
              onPress={() => setHasPets(false)}
              activeOpacity={0.8}
            >
              <RadioButton selected={hasPets === false} />
              <Text style={styles.radioLabel}>No</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Additional Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Additional</Text>
          <View style={styles.additionalInput}>
            <TextInput
              style={styles.additionalTextInput}
              value={specialInstructions}
              onChangeText={setSpecialInstructions}
              placeholder="Special entry instructions"
              placeholderTextColor={PRIMITIVE_COLORS.gray300}
            />
          </View>
        </View>

        {/* Submit Button */}
        <TouchableOpacity
          style={[
            styles.submitButton,
            status.isLoading && styles.submitButtonDisabled,
          ]}
          onPress={handleSubmit}
          disabled={status.isLoading}
          activeOpacity={0.8}
        >
          <Text style={styles.submitButtonText}>
            {status.isLoading ? 'Submitting...' : 'Submit work request'}
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: PRIMITIVE_COLORS.gray900,
  },
  contentContainer: {
    flexGrow: 1,
  },

  // Header
  header: {
    height: 93,
    borderBottomWidth: 1,
    borderBottomColor: PRIMITIVE_COLORS.gray700,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: 24,
    width: 40,
    height: 40,
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 30,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 45,
    textAlign: 'center',
  },

  // Form Container
  formContainer: {
    width: 866,
    alignSelf: 'center',
    paddingHorizontal: 32,
    paddingVertical: 40,
    gap: 40,
  },

  // Section
  section: {
    gap: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 24,
  },

  // Category
  categoryRow: {
    flexDirection: 'row',
    gap: 24,
  },
  categoryCard: {
    flex: 1,
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderRadius: 8,
    padding: 16,
    paddingBottom: 20,
    flexDirection: 'row',
    gap: 16,
  },
  categoryCardSelected: {
    // No visual change needed, subcategory dropdown shows
  },
  categoryIconContainer: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: PRIMITIVE_COLORS.white10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryContent: {
    flex: 1,
    gap: 6,
  },
  categoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  categoryName: {
    fontSize: 14,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 21,
  },
  categoryDescription: {
    fontSize: 12,
    fontWeight: '300',
    color: PRIMITIVE_COLORS.gray300,
    lineHeight: 18,
  },
  subcategoryDropdown: {
    marginTop: 10,
  },

  // Severity Grid
  severityGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
  },
  severityItem: {
    width: '48%',
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  severityItemSelected: {
    // Visual is handled by radio button
  },
  severityText: {
    fontSize: 14,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 21,
    flex: 1,
  },

  // Details Row
  detailsRow: {
    flexDirection: 'row',
    gap: 24,
  },
  detailsLeft: {
    flex: 1,
    gap: 24,
  },
  detailsRight: {
    width: 389,
  },

  // Inputs
  inputContainer: {
    backgroundColor: PRIMITIVE_COLORS.gray700,
    borderWidth: 1,
    borderColor: PRIMITIVE_COLORS.gray500,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 8,
  },
  inputLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: PRIMITIVE_COLORS.gray300,
    lineHeight: 12.5,
  },
  titleInput: {
    fontSize: 14,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 17.5,
    ...(Platform.OS === 'web' && ({
      outlineStyle: 'none',
    } as any)),
  },
  descriptionContainer: {
    flex: 1,
    minHeight: 280,
    paddingTop: 11,
  },
  descriptionInput: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 21,
    ...(Platform.OS === 'web' && ({
      outlineStyle: 'none',
    } as any)),
  },

  // Radio Group
  radioGroup: {
    gap: 12,
  },
  radioColumn: {
    gap: 12,
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  radioLabel: {
    fontSize: 16,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 24,
  },

  // Time Slots
  timeSlotsContainer: {
    marginLeft: 26,
    gap: 16,
    width: 389,
  },
  alertBox: {
    backgroundColor: 'rgba(255, 118, 41, 0.1)',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  alertIcon: {
    fontSize: 16,
    color: PRIMITIVE_COLORS.warningOrange,
  },
  alertText: {
    fontSize: 14,
    fontWeight: '400',
    color: PRIMITIVE_COLORS.warningOrange,
    lineHeight: 21,
    flex: 1,
  },
  timeSlotInput: {
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderBottomWidth: 1,
    borderBottomColor: SEMANTIC_COLORS.text.primary,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    paddingHorizontal: 12,
    paddingTop: 6,
    paddingBottom: 4,
  },
  timeSlotLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: PRIMITIVE_COLORS.gray300,
    lineHeight: 12,
  },
  timeSlotValue: {
    fontSize: 16,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 24,
    ...(Platform.OS === 'web' && ({
      outlineStyle: 'none',
    } as any)),
  },

  // Additional
  additionalInput: {
    width: 389,
    backgroundColor: PRIMITIVE_COLORS.gray700,
    borderWidth: 1,
    borderColor: PRIMITIVE_COLORS.gray500,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  additionalTextInput: {
    fontSize: 14,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 17.5,
    ...(Platform.OS === 'web' && ({
      outlineStyle: 'none',
    } as any)),
  },

  // Submit Button
  submitButton: {
    backgroundColor: PRIMITIVE_COLORS.resioPink,
    borderRadius: 8,
    height: 40,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: PRIMITIVE_COLORS.gray950,
    lineHeight: 21,
  },
});

export default DesktopCreateMaintenanceView;
